import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { Paper } from '@gto/ui/paper'
import { colors, gradients, radius } from '@gto/ui/theme/design-tokens'
import { Typography } from '@gto/ui/typography'

import { useHomepageStatsRetrieve } from '@/api/gen'
import IconCash from '@/assets/images/cash-icon.png'
import IconMoneyBag from '@/assets/images/money-bag-icon.png'
import IconStar from '@/assets/images/star-icon.png'
import { AbsoluteSuitsOnCorners } from '@/components/absolute-suits-on-corners'
import { sendGaEvent } from '@/gtag'
import type { Translations } from '@/localise'

const icons = [IconStar, IconCash, IconMoneyBag]

export const PerksSection = () => {
  const { data } = useHomepageStatsRetrieve<{ max_rakeback_percentage: number }>({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      enabled: true,
      placeholderData: keepPreviousData,
    },
  })

  return (
    <section
      className="relative mt-12 flex flex-col items-center gap-16 px-8 py-12 sm:items-stretch sm:gap-12 sm:px-4"
      style={{
        border: `1px solid ${colors.divider}`,
        borderRadius: radius.med,
        background: gradients.primary.horizontal(0.001, 0.002),
      }}
    >
      <AbsoluteSuitsOnCorners top={-4} />
      <div className="text-center">
        <Typography
          gutterBottom
          color="primary"
          component="h2"
          variant="h3"
          className={cn(
            'relative whitespace-pre-line',
            "before:absolute before:inset-x-0 before:top-1/2 before:h-full before:bg-primary-main before:blur-[60px] before:content-['']",
          )}
        >
          <FormattedMessage id="home.perksSection.title" />
        </Typography>
        <Typography
          className="font-semibold"
          color="text.primary"
          component="h2"
          variant="h6"
        >
          <FormattedMessage id="home.perksSection.description" />
        </Typography>
      </div>
      <div className="flex w-[80%] flex-row justify-center gap-6 md:w-full md:flex-col">
        {Array(3)
          .fill(null)
          .map((_, index) => (
            <PerkCard
              // eslint-disable-next-line @eslint-react/no-array-index-key -- fixed 3-card render, never reorders
              key={index}
              icon={icons[index]}
              number={index}
              title={
                <FormattedMessage
                  id={`home.perksSection.perks.${index + 1}.title` as keyof Translations}
                  {...(index === 0 && {
                    values: {
                      percentage: data?.max_rakeback_percentage ?? 'x',
                    },
                  })}
                />
              }
            />
          ))}
      </div>
      <div>
        <Button
          className="sm:w-full"
          color="primary"
          component={Link}
          href="/app/deals"
          size="large"
          variant="contained"
          onClick={() => {
            sendGaEvent({
              event: 'home_unlock_your_rewards_now',
            })
          }}
        >
          <FormattedMessage id="common.controls.unlockYourRewards" />
        </Button>
      </div>
    </section>
  )
}

type PerkCardProps = {
  number: number
  icon?: string
  title: string | ReactNode
}

const PerkCard = ({ number, icon, title }: PerkCardProps) => {
  return (
    <div className="max-w-[25%] md:max-w-full">
      <div className="flex flex-col items-center gap-8">
        <Paper className="rounded-md border border-primary-main bg-black p-4">
          <div className="flex flex-col gap-2">
            <div className="flex size-[36px] flex-[0_0_auto] items-center justify-center rounded-full bg-paper-elevation4">
              <Typography
                className="font-semibold"
                color="primary"
              >
                {number + 1}
              </Typography>
            </div>
            <div className="ml-12 flex justify-end">
              <div className="size-[100px]">
                <img
                  alt={typeof title === 'string' ? title : ''}
                  loading="lazy"
                  src={icon}
                  style={{ display: 'block', maxWidth: '100%' }}
                />
              </div>
            </div>
          </div>
        </Paper>
        <Typography
          className="text-center font-semibold"
          component="p"
          variant="subtitle1"
        >
          {title}
        </Typography>
      </div>
    </div>
  )
}
